import { CalibrationIcon, Label, Text } from '@viapeople/components'
import { useNavigate } from 'react-router-dom'
import { type Column, GenericTable } from '../../../../admin/src/components/GenericTable'
import { toSentenceCase } from '../../utils/helpers'
import { trpc } from '../../utils/trpc'
import type { CalibrationSession } from './types'

type CalibrationSessionInput = {
	search?: string
	sortBy?: string
	order?: 'asc' | 'desc'
	perPage?: number
	page?: number
}

const resolveCalibrationStatusColor = (status: string): 'green' | 'yellow' | 'default' => {
	switch (status) {
		case 'completed':
			return 'green'
		case 'in-progress':
			return 'yellow'
		case 'submitted':
			return 'yellow'
		default:
			return 'default'
	}
}

export const CalibrationList = () => {
	const navigate = useNavigate()

	const columns: Column<CalibrationSession>[] = [
		{
			field: 'name',
			label: 'Session Name',
			colSpan: 3,
			render: (session: CalibrationSession) => (
				<Text variant='xsmall' weight='semibold' className='group-hover:text-primary-600'>
					{session.name}
				</Text>
			),
		},
		{
			field: 'cycleName',
			label: 'Cycle',
			colSpan: 2,
			render: (session: CalibrationSession) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{session.cycleName}
				</Text>
			),
		},
		{
			field: 'participantsCount',
			label: 'Self Evaluators',
			colSpan: 1,
			render: (session: CalibrationSession) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{session.participantsCount || 0}
				</Text>
			),
		},
		{
			field: 'createdAt',
			label: 'Created',
			colSpan: 2,
			render: (session: CalibrationSession) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{new Date(session.createdAt).toLocaleDateString('en-US', {
						year: 'numeric',
						month: 'short',
						day: 'numeric',
					})}
				</Text>
			),
		},
		{
			field: 'status',
			label: 'Status',
			colSpan: 2,
			render: (session: CalibrationSession) => (
				<Label className='w-25!' color={resolveCalibrationStatusColor(session.status)}>
					{toSentenceCase(session.status)}
				</Label>
			),
		},
	]

	const queryOptionsGenerator = (options: CalibrationSessionInput) => ({
		...trpc.client.calibrations.getSessions.queryOptions(options),
	})

	const handleRowClick = (session: CalibrationSession) => {
		navigate(`/calibration/${session.cycleId}/${session.id}`)
	}

	return (
		<main className='w-full p-6'>
			<div className='mb-6'>
				<Text variant='h3' weight='semibold'>
					Calibration Sessions
				</Text>
			</div>

			<div className='border border-neutral-200 bg-neutral-0 rounded-lg shadow-xl'>
				<GenericTable<CalibrationSession, CalibrationSessionInput>
					columns={columns}
					idField='id'
					queryOptionsGenerator={queryOptionsGenerator}
					onRowClick={handleRowClick}
					showSearch
					showPagination
					emptyState={{
						icon: CalibrationIcon,
						title: 'No calibration sessions',
						description: 'Calibration sessions will appear here when they are created',
					}}
				/>
			</div>
		</main>
	)
}
